import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, AlertTriangle, MapPin, Clock, Megaphone, Loader2 } from "lucide-react";
import { api, mediaUrl } from "../lib/api";

/**
 * Full broadcast view a citizen lands on after tapping the live banner
 * (or pasting the URL). Pulls /api/broadcasts/{id} which is a public
 * GET — no auth needed because broadcasts are by design meant for the
 * whole population.
 */

const SEVERITY = {
  critical: { tag: "CRITIQUE", color: "bg-rose-600", border: "border-rose-700" },
  warning:  { tag: "AVERTISSEMENT", color: "bg-amber-500", border: "border-amber-600" },
  info:     { tag: "INFORMATION", color: "bg-cyan-600", border: "border-cyan-700" },
};

const fmt = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }); }
  catch (_e) { return iso; }
};

export default function BroadcastDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bc, setBc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    // Defer state updates out of the effect body to avoid the
    // set-state-in-effect cascading-renders rule.
    Promise.resolve().then(() => {
      if (!mounted) return;
      setLoading(true);
      api.get(`/broadcasts/${id}`)
        .then((r) => { if (mounted) setBc(r.data); })
        .catch((e) => {
          if (mounted) setError(e?.response?.data?.detail || "Diffusion introuvable");
        })
        .finally(() => { if (mounted) setLoading(false); });
    });
    return () => { mounted = false; };
  }, [id]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center py-12 text-rescue-mute" data-testid="broadcast-detail-loading">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }
  if (error || !bc) {
    return (
      <div className="flex-1 px-5 py-8 text-center" data-testid="broadcast-detail-error">
        <AlertTriangle className="w-10 h-10 text-rescue-mute mx-auto mb-2" />
        <div className="font-display text-lg font-black tracking-tight text-rescue-ink">Diffusion introuvable</div>
        <div className="text-xs text-rescue-mute mt-1">{error || "Cette diffusion a peut-être expiré."}</div>
        <button
          type="button"
          onClick={() => navigate("/")}
          className="mt-4 border-2 border-rescue-ink text-rescue-ink px-3 py-2 text-[10px] font-bold uppercase tracking-[0.2em]"
        >
          ← Retour à l&apos;accueil
        </button>
      </div>
    );
  }

  const sev = SEVERITY[bc.severity] || SEVERITY.info;

  return (
    <div className="flex-1 flex flex-col" data-testid="broadcast-detail">
      <div className={`${sev.color} text-white px-3 py-3 flex items-start gap-2`}>
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="broadcast-back-btn"
          className="w-7 h-7 grid place-items-center bg-black/30 hover:bg-black/50 shrink-0 mt-0.5"
          aria-label="Retour"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Megaphone className="w-4 h-4" strokeWidth={2.4} />
            <span className="text-[9px] font-bold tracking-[0.25em] uppercase opacity-90">{sev.tag}</span>
          </div>
          <h1 className="font-display text-2xl font-black tracking-tight leading-tight mt-1 break-words" data-testid="broadcast-detail-title">
            {bc.title}
          </h1>
        </div>
      </div>

      <div className="px-4 py-4 space-y-4">
        <div className="text-xs text-rescue-mute flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            <span data-testid="broadcast-detail-time">{fmt(bc.created_at)}</span>
          </span>
          {bc.expires_at && (
            <span className="inline-flex items-center gap-1 text-amber-700">
              <Clock className="w-3.5 h-3.5" />
              Expire {fmt(bc.expires_at)}
            </span>
          )}
        </div>

        <div className="bg-rescue-bg border-2 border-rescue-line p-4">
          <p className="text-sm text-rescue-ink leading-relaxed whitespace-pre-wrap" data-testid="broadcast-detail-message">
            {bc.message}
          </p>
        </div>

        <div className="border-2 border-rescue-line bg-white px-4 py-3">
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-rescue-mute mb-1">Zone concernée</div>
          {bc.map_preview_url && (
            <img
              src={mediaUrl(bc.map_preview_url)}
              alt="Carte de la zone concernée"
              data-testid="broadcast-detail-map-preview"
              className="w-full h-48 object-cover border-2 border-rescue-line mb-3"
            />
          )}
          <div className="flex items-center gap-2 text-rescue-ink">
            <MapPin className="w-4 h-4" />
            <span className="font-mono text-sm tabular-nums">
              {bc.latitude?.toFixed(4)}, {bc.longitude?.toFixed(4)}
            </span>
            <span className="text-rescue-mute">·</span>
            <span className="font-bold">{bc.radius_km} km</span>
          </div>
        </div>

        {Array.isArray(bc.attachments) && bc.attachments.length > 0 && (
          <div className="space-y-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-rescue-mute">Pièces jointes</div>
            <div className="grid grid-cols-3 gap-2" data-testid="broadcast-detail-attachments">
              {bc.attachments.map((a, idx) => (
                <a
                  key={`${a.url}-${idx}`}
                  href={mediaUrl(a.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="aspect-square border-2 border-rescue-line bg-rescue-bg overflow-hidden block"
                  title={a.name}
                >
                  {a.kind === "image" ? (
                    <img src={mediaUrl(a.url)} alt={a.name || ""} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[9px] font-bold uppercase text-rescue-mute">
                      {a.kind}
                    </div>
                  )}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
